import { OrbitControls, useGLTF, Html } from '@react-three/drei';
import { DirectionalLightHelper } from 'three';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useProgress, Environment, Text } from '@react-three/drei';
import { useControls } from 'leva';
import { useThree, useFrame } from '@react-three/fiber'
import { db, auth } from '../firebase-config';
import { doc, getDoc } from 'firebase/firestore';

const MODEL_URL = '/assets/hall/hall.glb';

const produit_1 = '/assets/salle_1/produits/pomme_dauphine.glb';
const produit_2 = '/assets/salle_1/produits/pomme_dauphine.glb';
const produit_3 = '/assets/salle_1/produits/pomme_dauphine.glb';

useGLTF.preload(MODEL_URL);
useGLTF.preload(produit_1);
useGLTF.preload(produit_2);
useGLTF.preload(produit_3);

function Loader() {
  const { progress } = useProgress();
  return (
    <Html center>
        <div style={{ fontSize: '1.5rem', color: '#333' }}>
            Chargement : {Math.floor(progress)}%
        </div>
    </Html>
  );
}

function Scene() {
    const gltf = useGLTF(MODEL_URL);

    const { position, rotation, scale } = useControls('Modèle', {
        position: { value: [0.8, -1, 4], step: 0.1 },
        rotation: { value: [0, 3.15, 0], step: 0.01 },
        scale: { value: 1, min: 0.01, max: 10 },
    });

    return (
        <primitive
            object={gltf.scene}
            position={position}
            rotation={rotation}
            scale={scale}
        />
    );
}

function CameraControls() {
    const { camera } = useThree();

    const { camX, camY, camZ, fov } = useControls('Caméra', {
        camX: { value: 0.11, min: -20, max: 20, step: 0.01 },
        camY: { value: 2.26, min: -20, max: 20, step: 0.01 },
        camZ: { value: 8.6, min: -20, max: 20, step: 0.01 },
        fov: { value: 45, min: 10, max: 120, step: 1 },
    });

    useEffect(() => {
        camera.position.set(camX, camY, camZ);
        camera.fov = fov;
        camera.updateProjectionMatrix();
    }, [camX, camY, camZ, fov, camera]);

    return null;
}

function Produit({ url, position, label, onSelect }) {
    const { scene } = useGLTF(url);

    const { posX, posY, posZ } = useControls('Obj rotation', {
        posX: { value: 0, min: -20, max: 20, step: 0.01 },
        posY: { value: 0, min: -20, max: 20, step: 0.01 },
        posZ: { value: 0, min: -20, max: 20, step: 0.01 },
    });

    return (
        <group
            position={position}
            onClick={() => {
                console.log(`Produit sélectionné : ${label}`);
                onSelect(); 
            }}
            onPointerOver={() => (document.body.style.cursor = 'pointer')}
            onPointerOut={() => (document.body.style.cursor = 'default')}
        >
            <primitive object={scene.clone()}  rotation={[0, 3.15, 0]} scale={1} />
            <Text
                position={[0, 0.7, 0]}
                fontSize={0.2}
                color="white"
                anchorX="center"
                anchorY="middle"
            >
                {label}
            </Text>
        </group>
    );
}



function ProduitFocus({ url, label, onClose, resetCameraTo }) {
    const [selectedType, setSelectedType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [choixExistants, setChoixExistants] = useState({});
    const { camera } = useThree();

    const { scene } = useGLTF(url);
    const ref = useRef();

    const isDragging = useRef(false);
    const velocity = useRef(0);         
    const previousX = useRef(0);
    const previousY = useRef(0);
    const friction = 0.92;


    useFrame(() => {
        if (ref.current) {
            ref.current.rotation.y += velocity.current;
            if (!isDragging.current) {
                velocity.current *= friction;
                if (Math.abs(velocity.current) < 0.0001) velocity.current = 0;
            }
        }
    });

    const handlePointerDown = (e) => {
        isDragging.current = true;
        previousX.current = e.clientX;
        previousY.current = e.clientY;
    };

    const handlePointerUp = () => {
        isDragging.current = false;
    };

    const handlePointerMove = (e) => {
        if (!isDragging.current || !ref.current) return;

        const deltaX = e.clientX - previousX.current;
        const deltaY = e.clientY - previousY.current;

        previousX.current = e.clientX;
        previousY.current = e.clientY;

        ref.current.rotation.y += deltaX * 0.01;
        ref.current.rotation.x += deltaY * 0.01;

        const limit = Math.PI / 2;
        ref.current.rotation.x = Math.max(-limit, Math.min(limit, ref.current.rotation.x));
    };

    const handleValidate = async () => {
        if (!selectedType) return;

        const user = auth.currentUser;
        if (!user) return alert("Non connecté");

        if (choixExistants[selectedType]) {
            alert(`Vous avez déjà choisi un ${selectedType}.`);
            return;
        }

        setLoading(true);
        try {
            const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';

            const res = await fetch(`${BACKEND_URL}/api/choix-produits`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: user.uid,
                    email: user.email,
                    type: selectedType,
                    produit: label
                })
            });

            if (!res.ok) {
                const error = await res.json();
                console.error('❌ Backend error:', error.error);
                alert("Erreur côté serveur : " + error.error);
                return; // Ne pas fermer
            }

            const result = await res.json();
            console.log('✅ Choix produit envoyé via backend');

            // Met à jour l'état local pour bloquer une nouvelle réponse
            setChoixExistants((prev) => ({
                ...prev,
                [selectedType]: label,
            }));

            setTimeout(onClose, 5000);
        } catch (e) {
            console.error('❌ Network error', e);
            alert("Erreur réseau : " + e.message); 
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const fetchChoix = async () => {
            const user = auth.currentUser;
            if (!user) return;

            const snap = await getDoc(doc(db, 'Salle_1', user.uid));
            if (snap.exists()) {
            const data = snap.data();
            setChoixExistants({
                apéritif: data.apéritif,
                repas: data.repas,
            });
            }
        };

        fetchChoix();
    }, []);

    useEffect(() => {
        if (resetCameraTo) {
            const [x, y, z] = resetCameraTo;
            camera.position.set(x, y, z);
            camera.lookAt(0, 1.5, 0); 
            camera.updateProjectionMatrix();
        }
    }, [resetCameraTo]);


    return (
        <>
            <group
                position={[1, 2, 6]}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerMove={handlePointerMove}
            >
                <mesh>
                    <boxGeometry args={[5, 5, 5]} />
                    <meshBasicMaterial transparent opacity={0} />
                </mesh>

                <primitive object={scene.clone()} rotation={[0, 3.15, 0]} ref={ref} scale={2} />

                <mesh position={[-1.2, -0.4, -3]} rotation={[-0.08, 0, 0]}>
                    <planeGeometry args={[8, 4]} />
                    <meshBasicMaterial color="white" transparent opacity={0.95} />
                </mesh>
            </group>

            <Html fullscreen>
                <div className="display_text_div_first_room">
                    <button className="button_close_first_room" onClick={onClose} style={{cursor: loading ? 'not-allowed' : 'pointer'}}>
                        <svg  xmlns="http://www.w3.org/2000/svg"  width="30"  height="30"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  class="icon icon-tabler icons-tabler-outline icon-tabler-x"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>
                    </button>
                    <h3>{label}</h3>

                    {/* <img
                        src="/assets/salle_1/images/produit_1/image_1.png"
                        style={{ width: '100%', marginBottom: '10px' }}
                    />
                    <img
                        src="/assets/salle_1/images/produit_1/image_2.png"
                        style={{ width: '100%' }}
                    /> */}
                    <div className="choice_div_first_room">
                        {['apéritif', 'repas'].map((type) => (
                            <div
                                key={type}
                                onClick={() => setSelectedType(type)}
                            >
                                <p 
                                    className="choice_element_text"
                                    style={{
                                        border: selectedType === type ? '2px solid rgb(92 211 114)' : '2px solid #fff',
                                        cursor: 'pointer',
                                        background: selectedType === type ? 'rgb(92 211 114 / 34%)' : '#fff'
                                    }}
                                >
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </p>
                            </div>
                        ))}
                    </div>

                    {choixExistants.apéritif && (
                        <p style={{ fontSize: '0.8rem' }}>
                            ✅ Apéritif sélectionné : {choixExistants.apéritif}
                        </p>
                    )}
                    {choixExistants.repas && (
                        <p style={{ fontSize: '0.8rem' }}>
                            ✅ Repas sélectionné : {choixExistants.repas}
                        </p>
                    )}

                    <div className="button_validate_first_room_div">
                        <button
                            onClick={handleValidate}
                            disabled={!selectedType || loading || choixExistants[selectedType]}
                            className="button_validate_first_room"
                            style={{
                                pointerEvents: choixExistants[selectedType] ? 'none' : 'auto',
                                opacity: choixExistants[selectedType] ? 0.5 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer'
                            }}
                            >
                            {loading ? 'Envoi...' : 'Valider'}
                        </button>
                    </div>

                    {/* Message explicite si déjà répondu */}
                    {selectedType && choixExistants[selectedType] && (
                        <p style={{ color: '#c00', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                            Vous avez déjà sélectionné un {selectedType} : <strong>{choixExistants[selectedType]}</strong>
                        </p>
                    )}


                </div>
            </Html>
        </>
    );
}


export default function Experience() {
    const [selectedProduit, setSelectedProduit] = useState(null); 

    const { intensity, lightX, lightY, lightZ } = useControls('Lumière', {
        intensity: { value: 1, min: 0, max: 5, step: 0.1 },
        lightX: { value: 5, min: -10, max: 10 },
        lightY: { value: 10, min: -10, max: 20 },
        lightZ: { value: 5, min: -10, max: 10 },
    });

    return (
        <>
            Site ferme
        </>
    );
}
